import type {
  CartView,
  CatalogItem,
  CatalogOptionGroup,
  CouponChange,
  PaymentPreference,
  SaveDraftPayload,
} from '@/lib/draft-order-client';

/**
 * The operator's local working copy of a cart.
 *
 * The panel does not edit the server's cart directly: changes are staged here behind an "unsaved
 * changes" banner and flushed as one snapshot, so an operator can discard a half-finished edit and
 * the agent never sees an intermediate state. Everything in this file is pure — the diffing and the
 * payload construction are the only things that decide what a save actually means.
 */

export interface DraftLineOption {
  groupId: string;
  optionId: string;
  groupName: string;
  optionName: string;
  priceDelta: number;
}

export interface DraftLine {
  /** Stable across edits so React keeps focus while a row is being changed. */
  key: string;
  catalogItemId: string;
  name: string;
  quantity: number;
  options: DraftLineOption[];
  comments: string;
  /** What the order currently stands at for this line. Never the catalog's price — see `newUnitPrice`. */
  unitPrice: number;
  /**
   * The menu price now, when the item was repriced under this cart. Display only: saving is what
   * re-prices the line, so every total here stays on `unitPrice` until then.
   */
  newUnitPrice?: number;
  available?: boolean;
  orphaned: boolean;
}

export interface DraftWorkingCopy {
  lines: DraftLine[];
  deliveryType?: 'delivery' | 'pickup';
  /**
   * The branch the order is for. Only ever settable while the cart has none — once a branch is
   * pinned the server refuses to re-point it, because the customer was quoted that branch's menu,
   * hours and delivery zone.
   */
  locationId?: string;
  address: string;
  instructions: string;
  savedAddressId?: string;
  /** Empty string = leave the zone engine in charge. */
  deliveryFeeOverride: string;
  paymentPreference?: PaymentPreference;
  useSavedCard: boolean;
  notes: string;
  tipAmount: string;
}

let keyCounter = 0;
const nextKey = () => `line-${(keyCounter += 1)}`;

export function workingCopyFromCart(cart: CartView): DraftWorkingCopy {
  return {
    lines: cart.items.map((item) => ({
      key: nextKey(),
      catalogItemId: item.catalogItemId,
      name: item.name,
      quantity: item.quantity,
      // An option recorded before the ids were persisted cannot be round-tripped through the
      // catalog, so it is dropped here rather than sent back as something the resolver will reject.
      options: (item.options ?? []).flatMap((option) =>
        option.groupId && option.optionId
          ? [
              {
                groupId: option.groupId,
                optionId: option.optionId,
                groupName: option.groupName,
                optionName: option.optionName,
                priceDelta: option.priceDelta,
              },
            ]
          : [],
      ),
      comments: item.comments ?? '',
      unitPrice: item.unitPrice,
      newUnitPrice: item.catalogUnitPrice,
      available: item.available,
      orphaned: item.orphaned,
    })),
    deliveryType: cart.deliveryType,
    locationId: cart.locationId,
    address: cart.deliveryAddress?.raw ?? '',
    instructions: cart.deliveryAddress?.instructions ?? '',
    savedAddressId: undefined,
    deliveryFeeOverride: '',
    paymentPreference: cart.paymentPreference,
    useSavedCard: cart.payment?.storedCard?.selected ?? false,
    notes: cart.notes ?? '',
    tipAmount: cart.tipAmount ? String(cart.tipAmount) : '',
  };
}

export function lineFromCatalogItem(
  item: CatalogItem,
  selection: { quantity: number; options: DraftLineOption[]; comments: string },
): DraftLine {
  return {
    key: nextKey(),
    catalogItemId: item.id,
    name: item.name,
    quantity: selection.quantity,
    options: selection.options,
    comments: selection.comments,
    unitPrice: item.promoPrice ?? item.price,
    available: item.available,
    orphaned: false,
  };
}

export function lineSubtotal(line: DraftLine): number {
  const optionsDelta = line.options.reduce((sum, option) => sum + option.priceDelta, 0);
  return (line.unitPrice + optionsDelta) * line.quantity;
}

export function linesTotal(lines: DraftLine[]): number {
  return lines.reduce((sum, line) => sum + lineSubtotal(line), 0);
}

/**
 * The total the panel shows while editing. Approximate by construction — the delivery fee and the
 * coupon discount are only re-derived server-side on save — which is exactly why the banner tells
 * the operator the changes are not saved yet.
 *
 * The discounts are carried at their last server-computed SUM (`cart.discountAmount`) because the
 * applied-discount snapshots carry no type or value to reprice a percentage against. That amount
 * goes stale the moment lines are edited, but omitting it is strictly worse — it would show a total
 * ABOVE what the customer is charged, and this figure is what feeds the "total changed, ask the
 * customer" advice. Clamped at zero so a discount larger than the remaining lines cannot go negative.
 */
export function projectedTotal(copy: DraftWorkingCopy, cart: CartView): number {
  return amountBeforeTip(copy, cart) + (parseAmount(copy.tipAmount) ?? 0);
}

/**
 * The amount due before the tip — items less discounts plus the net delivery fee — as the panel
 * projects it while editing (same approximation as {@link projectedTotal}). It is the tip's bound:
 * the server refuses a tip above its own figure for it with `TIP_NOT_ALLOWED` (D2, D3).
 */
export function amountBeforeTip(copy: DraftWorkingCopy, cart: CartView): number {
  const override = parseAmount(copy.deliveryFeeOverride);
  const pickup = copy.deliveryType === 'pickup';
  const fee = override ?? (pickup ? 0 : (cart.deliveryFee ?? 0));
  // The fee share rides on the fee, never on the lines: a staged switch to pickup drops it with the
  // fee, and an override can only shrink it to the fee it would come off.
  const feeDiscount = pickup ? 0 : Math.min(cart.deliveryFeeDiscountAmount ?? 0, fee);
  const discount = Math.min(itemsDiscountAmount(cart), linesTotal(copy.lines));
  return linesTotal(copy.lines) - discount + fee - feeDiscount;
}

/**
 * Why the staged tip cannot be saved, or null: it must be a number with `0 ≤ tip ≤` the amount due
 * before it. The operator is trusted on the amount itself — only the bound applies (D3) — and the
 * server re-checks it on save regardless.
 */
export function tipAmountError(copy: DraftWorkingCopy, cart: CartView): 'invalid' | 'aboveMax' | null {
  if (!copy.tipAmount.trim()) return null;
  const tip = parseAmount(copy.tipAmount);
  if (tip === undefined) return 'invalid';
  // Both sides at 2 dp, as the server rounds the tip before comparing it (D3).
  return roundMoney(tip) > roundMoney(amountBeforeTip(copy, cart)) + 1e-9 ? 'aboveMax' : null;
}

function roundMoney(amount: number): number {
  return Math.round(Math.max(0, amount) * 100) / 100;
}

/**
 * The part of the cart's discount that came off the items (and the loyalty line) — `discountAmount`
 * minus its fee share. What every reader that compares a discount against the lines must use.
 */
export function itemsDiscountAmount(cart: Pick<CartView, 'discountAmount' | 'deliveryFeeDiscountAmount'>): number {
  return Math.max(0, (cart.discountAmount ?? 0) - (cart.deliveryFeeDiscountAmount ?? 0));
}

/** The fee the customer actually pays: the quoted fee less the discounts' fee share, never below 0. */
export function netDeliveryFee(cart: Pick<CartView, 'deliveryFee' | 'deliveryFeeDiscountAmount'>): number {
  return Math.max(0, (cart.deliveryFee ?? 0) - (cart.deliveryFeeDiscountAmount ?? 0));
}

export function parseAmount(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

/**
 * The cart summary as a message to the CUSTOMER: the backend's `cartSummary` with the delivery fee
 * and tip made visible above the total. That Total bakes both in (see the backend's
 * `calculateTotal`), so sending the block without them reads like a mistake — lines that don't add up
 * to their own total. Labels are arguments rather than a `t` dependency because the message goes out
 * in the operator's UI language, exactly like the intro/question composed around it.
 *
 * The DISCOUNT is deliberately not one of them: `buildCartSummary` (backend) prints its own signed,
 * code-named discount line, so adding one here showed the customer two of them for one coupon. Only
 * the fee and tip are genuinely missing from that string.
 */
export function customerCartSummary(
  cart: Pick<
    CartView,
    'cartSummary' | 'deliveryType' | 'deliveryFee' | 'deliveryFeeDiscountAmount' | 'tipAmount' | 'currencySymbol'
  >,
  labels: { deliveryFee: string; tip: string; freeDelivery: string },
): string {
  const extras: string[] = [];
  if (cart.deliveryType === 'delivery' && cart.deliveryFee != null) {
    // NET of the fee discount: the backend's discount line prints only its items money signed and
    // names the fee part in words ("+ envío gratis"), so the gross fee here would be counted twice.
    const fee = netDeliveryFee(cart);
    const feeDiscounted = (cart.deliveryFeeDiscountAmount ?? 0) > 0;
    extras.push(
      `${labels.deliveryFee}: ${feeDiscounted && fee === 0 ? labels.freeDelivery : `${cart.currencySymbol}${fee.toFixed(2)}`}`,
    );
  }
  if (cart.tipAmount) {
    extras.push(`${labels.tip}: ${cart.currencySymbol}${cart.tipAmount.toFixed(2)}`);
  }
  if (extras.length === 0) return cart.cartSummary;

  // `buildCartSummary` (backend) always closes with its "Total: $X" line; the amounts slot in
  // right above it. If that shape ever changes, appending is the graceful wrong answer.
  const lines = cart.cartSummary.split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    if (lines[i].startsWith('Total:')) {
      lines.splice(i, 0, ...extras);
      return lines.join('\n');
    }
  }
  return [...lines, ...extras].join('\n');
}

// ─── Dirty tracking ────────────────────────────────────────────

export interface DirtySections {
  items: boolean;
  delivery: boolean;
  payment: boolean;
  extras: boolean;
  /**
   * The menu was repriced under this cart. Not an operator edit — nobody staged it — but it does mean
   * the order stands at a total that is no longer the menu's, which only a save can reconcile.
   */
  priceDrift: boolean;
  /** The operator changed something. Drives the unsaved-changes banner, pauses polling, gates Confirmar. */
  any: boolean;
  /** There is something for Guardar to do, whoever caused it. Gates Guardar only — drift alone never blocks Confirmar. */
  saveable: boolean;
}

export function diffAgainstCart(copy: DraftWorkingCopy, cart: CartView): DirtySections {
  const items = serializeLines(copy.lines) !== serializeLines(workingCopyFromCart(cart).lines);

  const delivery =
    copy.deliveryType !== cart.deliveryType ||
    copy.locationId !== cart.locationId ||
    copy.address.trim() !== (cart.deliveryAddress?.raw ?? '').trim() ||
    copy.instructions.trim() !== (cart.deliveryAddress?.instructions ?? '').trim() ||
    Boolean(copy.savedAddressId) ||
    parseAmount(copy.deliveryFeeOverride) !== undefined;

  const payment =
    copy.paymentPreference !== cart.paymentPreference ||
    copy.useSavedCard !== (cart.payment?.storedCard?.selected ?? false);

  const extras =
    copy.notes.trim() !== (cart.notes ?? '').trim() || (parseAmount(copy.tipAmount) ?? 0) !== (cart.tipAmount ?? 0);

  // Read off the server's cart, not the working copy: it is the persisted lines that are mispriced,
  // and a line the operator has already re-staged from the catalog carries the new price anyway.
  const priceDrift = cart.items.some((item) => item.catalogUnitPrice !== undefined);

  const any = items || delivery || payment || extras;
  return { items, delivery, payment, extras, priceDrift, any, saveable: any || priceDrift };
}

/** Order-sensitive on purpose: moving a line is a change the operator made and should be able to save. */
function serializeLines(lines: DraftLine[]): string {
  return JSON.stringify(
    lines.map((line) => [
      line.catalogItemId,
      line.quantity,
      line.comments.trim(),
      line.options.map((o) => `${o.groupId}:${o.optionId}`).sort(),
    ]),
  );
}

// ─── Save payload ──────────────────────────────────────────────

/**
 * Only the sections the operator actually touched. Sending an untouched section back would re-run
 * its rules for no reason — re-geocoding an unchanged address, or re-asserting a payment method the
 * agent may have legitimately moved on from in the meantime.
 */
export function buildSavePayload(copy: DraftWorkingCopy, cart: CartView, dirty: DirtySections): SaveDraftPayload {
  const payload: SaveDraftPayload = { expectedUpdatedAt: cart.updatedAt };

  // Drift counts as a reason to send the lines even when the operator changed none of them: the
  // server re-prices every line it receives from the catalog, so this IS the re-pricing.
  if (dirty.items || dirty.priceDrift) {
    payload.items = copy.lines.map((line) => ({
      catalogItemId: line.catalogItemId,
      quantity: line.quantity,
      ...(line.options.length > 0
        ? { options: line.options.map((o) => ({ groupId: o.groupId, optionId: o.optionId })) }
        : {}),
      ...(line.comments.trim() ? { comments: line.comments.trim() } : {}),
    }));
  }

  if (dirty.delivery) {
    const feeOverride = parseAmount(copy.deliveryFeeOverride);
    payload.delivery = {
      ...(copy.deliveryType ? { deliveryType: copy.deliveryType } : {}),
      // Only when it is genuinely new: the picker is disabled once a branch is pinned, so a value
      // that differs from the cart's can only have come from a cart that had none.
      ...(copy.locationId && copy.locationId !== cart.locationId ? { locationId: copy.locationId } : {}),
      ...(copy.savedAddressId
        ? { savedAddressId: copy.savedAddressId }
        : copy.address.trim()
          ? { address: copy.address.trim() }
          : {}),
      ...(copy.instructions.trim() ? { instructions: copy.instructions.trim() } : {}),
      ...(feeOverride !== undefined ? { deliveryFeeOverride: feeOverride } : {}),
    };
  }

  if (dirty.payment) {
    payload.payment = {
      ...(copy.paymentPreference ? { paymentPreference: copy.paymentPreference } : {}),
      ...(cart.payment?.storedCard ? { useSavedCard: copy.useSavedCard } : {}),
    };
  }

  if (dirty.extras) {
    payload.notes = copy.notes.trim();
    payload.tipAmount = parseAmount(copy.tipAmount) ?? 0;
  }

  return payload;
}

// ─── Option-group validation (mirrors CartLineResolverService) ──

/**
 * Required groups the current selection has not satisfied. The client mirror of the server rule so
 * the Add button can stay disabled instead of letting the operator submit something that will bounce;
 * the server re-validates regardless.
 */
export function missingRequiredGroups(item: CatalogItem, selected: DraftLineOption[]): CatalogOptionGroup[] {
  return (item.optionGroups ?? []).filter((group) => {
    if (!group.required) return false;
    const chosen = selected.filter((option) => option.groupId === group.id).length;
    return chosen < (group.minChoices ?? 1);
  });
}

export function isSingleChoiceGroup(group: CatalogOptionGroup): boolean {
  return (group.maxChoices ?? (group.required ? 1 : group.options.length)) === 1;
}

// ─── Coupon changes from a branch move ──

/**
 * The notice for one coupon a save's branch move touched, as a `conversations` key (interpolated with
 * `code`) and a toast tone: a swap kept the discount, a removal took it off the total.
 */
export function couponChangeNotice(change: CouponChange): {
  key: 'cart.toasts.couponSwapped' | 'cart.toasts.couponRemoved';
  tone: 'info' | 'warning';
} {
  return change.outcome === 'swapped'
    ? { key: 'cart.toasts.couponSwapped', tone: 'info' }
    : { key: 'cart.toasts.couponRemoved', tone: 'warning' };
}
